import { GraphQLClient } from "graphql-request"
import { useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil"
import {
  ActivateAccountDocument,
  ActivateAccountMutation,
  ActivateAccountMutationVariables,
  GetPendingUserInviteDocument,
  GetPendingUserInviteQuery,
  GetPendingUserInviteQueryVariables,
  GetUserOrganizationAdminsDocument,
  GetUserOrganizationAdminsQuery,
  GetUserOrganizationAdminsQueryVariables,
  InsertLogDocument,
  InsertLogMutation,
  InsertLogMutationVariables,
  InsertOrganizationAdminDocument,
  InsertOrganizationAdminMutation,
  InsertOrganizationAdminMutationVariables,
  InsertOrganizationMemberDocument,
  InsertOrganizationMemberMutation,
  InsertOrganizationMemberMutationVariables,
  InsertSupervisorDocument,
  InsertSupervisorMutation,
  InsertSupervisorMutationVariables,
  UpdateUserInviteStatusDocument,
  UpdateUserInviteStatusMutation,
  UpdateUserInviteStatusMutationVariables,
} from "../generated/graphql"
import { useApi } from "../utils/API"
import { LogType, Role } from "../utils/constants"
import { ENDPOINTS } from "../utils/endpoints"
import {
  loggingOutState,
  hideDisconnectedClientsState,
  sessionState,
  currentRiskAlertPathState,
} from "../utils/store"
import { ErrorResponse } from "../utils/types"
import { useNavigation } from "./useNavigation"
import { useStorage } from "./useStorage"
import dayjs from "dayjs"

export const useSession = () => {
  const [session, setSession] = useRecoilState(sessionState)
  const resetHideDisconnectedClients = useResetRecoilState(
    hideDisconnectedClientsState
  )
  const setLoggingOut = useSetRecoilState(loggingOutState)
  const { storeSession, removeSession } = useStorage()
  const {
    toDashboard,
    toLoggedOut,
    toMyOrg,
    toRiskAlertDetail,
    toPractitioners,
  } = useNavigation()
  const API = useApi()
  const queryClient = useQueryClient()
  const [currentRiskAlertPath, setCurrentRiskAlertPath] = useRecoilState(
    currentRiskAlertPathState
  )

  async function insertLog(jwt: string, userId: string, type: string) {
    const client = new GraphQLClient(ENDPOINTS.GRAPHQL, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
    await client.request<InsertLogMutation, InsertLogMutationVariables>(
      InsertLogDocument,
      {
        type,
        user_id: userId,
      }
    )
  }

  const logout = useCallback(async () => {
    queryClient.invalidateQueries()
    queryClient.getQueryCache().clear()
    await insertLog(session?.jwt ?? "", session?.userId ?? "", LogType.SIGN_OUT)
    setLoggingOut(true)
    resetHideDisconnectedClients()
    setSession(undefined)
    removeSession()
    await API.logout()
    toLoggedOut()
  }, [
    API,
    queryClient,
    removeSession,
    resetHideDisconnectedClients,
    session?.jwt,
    session?.userId,
    setLoggingOut,
    setSession,
    toLoggedOut,
  ])

  return {
    register: useCallback(
      async (
        email: string,
        password: string,
        firstName: string,
        lastName: string,
        mobile: string
      ): Promise<ErrorResponse | string | undefined> => {
        const response = await API.register(email, password, {
          first_name: firstName,
          last_name: lastName,
          notification_time: new Date(),
        })
        if (response.status !== 200) {
          return response.data as unknown as ErrorResponse
        }
        const session = {
          jwt: response.data.jwt_token,
          userId: response.data.user.id,
          role: response.data.user.default_role,
        }
        storeSession(session)
        setSession(session)
        const client = new GraphQLClient(ENDPOINTS.GRAPHQL, {
          headers: { Authorization: `Bearer ${session.jwt}` },
        })
        const getUserInviteResponse = await client.request<
          GetPendingUserInviteQuery,
          GetPendingUserInviteQueryVariables
        >(GetPendingUserInviteDocument, {
          email,
        })
        if (
          !getUserInviteResponse ||
          (getUserInviteResponse &&
            getUserInviteResponse.user_invites.length === 0)
        ) {
          console.error(`Failed to find pending user invite for ${email}`)
          return
        }
        const invite = getUserInviteResponse.user_invites[0]
        await client.request<
          UpdateUserInviteStatusMutation,
          UpdateUserInviteStatusMutationVariables
        >(UpdateUserInviteStatusDocument, {
          id: invite.id,
          status: "registered",
        })
        await client.request<
          InsertOrganizationMemberMutation,
          InsertOrganizationMemberMutationVariables
        >(InsertOrganizationMemberDocument, {
          organization_id: invite.organization_id,
          practitioner_id: session.userId,
        })
        await client.request<
          ActivateAccountMutation,
          ActivateAccountMutationVariables
        >(ActivateAccountDocument, {
          email,
        })
        if (invite.role === Role.ADMIN) {
          await client.request<
            InsertOrganizationAdminMutation,
            InsertOrganizationAdminMutationVariables
          >(InsertOrganizationAdminDocument, {
            organization_id: invite.organization_id,
            practitioner_id: session.userId,
          })
        }
        if (invite.role === Role.WEB_ROLE) {
          await client.request<
            InsertSupervisorMutation,
            InsertSupervisorMutationVariables
          >(InsertSupervisorDocument, {
            supervisor: [
              {
                supervisor_id: session.userId,
                org_id: invite.organization_id,
                started_at: dayjs.utc(),
                ended_at: null,
              },
            ],
          })
        }
        if (!mobile) {
          // If the user does not have a mobile number, redirect them to the corresponding page
          await insertLog(
            response.data.jwt_token,
            response.data.user.id,
            LogType.SIGN_IN
          )
          if (invite.role === Role.ADMIN) {
            toMyOrg()
          } else if (invite.role === Role.WEB_ROLE) {
            toPractitioners()
          } else {
            toDashboard()
          }
        } else {
          return response.data.user.id + "@" + invite.role
        }
      },
      [API, setSession, storeSession, toDashboard, toMyOrg]
    ),

    logout,

    login: useCallback(
      async (email: string, password: string) => {
        const response = await API.login(email, password)
        if (response.status !== 200) {
          return response.data as unknown as ErrorResponse
        }
        if (
          ![Role.PRACTITIONER, Role.WEB_ROLE].includes(
            response.data.user.default_role as Role
          )
        ) {
          return {
            error: "",
            message: "User not found.",
            statusCode: 400,
          }
        }
        const session = {
          jwt: response.data.jwt_token,
          userId: response.data.user.id,
          role: response.data.user.default_role,
        }
        await insertLog(
          response.data.jwt_token,
          response.data.user.id,
          LogType.SIGN_IN
        )
        storeSession(session)
        setSession(session)

        const client = new GraphQLClient(ENDPOINTS.GRAPHQL, {
          headers: { Authorization: `Bearer ${session.jwt}` },
        })
        const getUserOrgAdminsResponse = await client.request<
          GetUserOrganizationAdminsQuery,
          GetUserOrganizationAdminsQueryVariables
        >(GetUserOrganizationAdminsDocument, {
          id: response.data.user.id,
        })
        const orgAdmins =
          getUserOrgAdminsResponse.users_by_pk?.organization_admins
        if (currentRiskAlertPath?.includes("risk-alert-detail")) {
          toRiskAlertDetail(currentRiskAlertPath)
          setCurrentRiskAlertPath(undefined)
        } else {
          if (orgAdmins && orgAdmins.length > 0) {
            toMyOrg()
          } else {
            if (response.data.user.default_role === Role.WEB_ROLE) {
              // Redirect practitioners to the dashboard
              toPractitioners()
            } else {
              // Redirect other roles to the dashboard
              toDashboard()
            }
          }
        }
      },
      [
        API,
        currentRiskAlertPath,
        setCurrentRiskAlertPath,
        setSession,
        storeSession,
        toDashboard,
        toMyOrg,
        toRiskAlertDetail,
        toPractitioners,
      ]
    ),

    session,
  }
}
